import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  ChannelBadge,
  type ChannelKind,
  ConfirmAction,
  EmptyState,
  Input,
  Label,
  ListPage,
  RefreshButton,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@skylabs-digital/backoffice-kit';
import { useSnackbar } from '@skylabs-digital/react-proto-kit';
import { AlertTriangle, Pencil, Plus, Trash2 } from 'lucide-react';
import type React from 'react';
import { useState } from 'react';

import type { ProviderConfigEntity, ProviderConfigInputType } from '../api/providerConfigs.api';
import { providerConfigsApi } from '../api/providerConfigs.api';
import { CHANNEL_OPTIONS } from '../constants/channel-colors';

/**
 * Canales cuya entrega falla en silencio (`NO_PROVIDER`) si no hay NINGUNA
 * fila `provider_configs` habilitada para ese canal. SMS/WHATSAPP/PUSH son
 * opcionales en el MVP (conectores condicionales en `backend/src/index.ts`),
 * por eso no entran en este chequeo.
 */
const REQUIRED_CHANNELS: readonly ChannelKind[] = ['EMAIL', 'WEBHOOK', 'IN_APP'];

/** IDs de conectores registrados hoy en `backend/src/index.ts`. */
const KNOWN_PROVIDER_IDS = [
  'resend-email',
  'twilio-sms',
  'twilio-whatsapp',
  'firebase-push',
  'default-inapp',
  'default-webhook',
] as const;

const EMPTY_FORM: ProviderConfigInputType = {
  channel: 'EMAIL',
  providerId: 'resend-email',
  priority: 1,
  isEnabled: true,
};

interface ProviderConfigFormProps {
  initial: ProviderConfigInputType;
  onSubmit: (data: ProviderConfigInputType) => void;
  onCancel: () => void;
  loading: boolean;
  submitLabel: string;
}

function ProviderConfigForm({
  initial,
  onSubmit,
  onCancel,
  loading,
  submitLabel,
}: ProviderConfigFormProps) {
  const [form, setForm] = useState<ProviderConfigInputType>(initial);
  const [customProviderId, setCustomProviderId] = useState(
    !KNOWN_PROVIDER_IDS.includes(initial.providerId as (typeof KNOWN_PROVIDER_IDS)[number]),
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(form);
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <div className="space-y-2">
              <Label htmlFor="pc-channel">Channel *</Label>
              <Select
                value={form.channel}
                onValueChange={(v) =>
                  setForm({ ...form, channel: v as ProviderConfigInputType['channel'] })
                }
              >
                <SelectTrigger id="pc-channel">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CHANNEL_OPTIONS.map((ch) => (
                    <SelectItem key={ch} value={ch}>
                      {ch}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="pc-provider">Provider ID *</Label>
              {customProviderId ? (
                <Input
                  id="pc-provider"
                  value={form.providerId}
                  onChange={(e) => setForm({ ...form, providerId: e.target.value })}
                  placeholder="custom-connector-id"
                  required
                />
              ) : (
                <Select
                  value={form.providerId}
                  onValueChange={(v) => {
                    if (v === '__custom__') {
                      setCustomProviderId(true);
                      setForm({ ...form, providerId: '' });
                      return;
                    }
                    setForm({ ...form, providerId: v });
                  }}
                >
                  <SelectTrigger id="pc-provider">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {KNOWN_PROVIDER_IDS.map((id) => (
                      <SelectItem key={id} value={id}>
                        {id}
                      </SelectItem>
                    ))}
                    <SelectItem value="__custom__">Custom…</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="pc-priority">Priority *</Label>
              <Input
                id="pc-priority"
                type="number"
                min={1}
                value={form.priority}
                onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value, 10) || 0 })}
                required
              />
            </div>
            <div className="flex items-end space-y-2">
              <label className="flex items-center gap-2 text-sm" htmlFor="pc-enabled">
                <Switch
                  id="pc-enabled"
                  checked={form.isEnabled ?? true}
                  onCheckedChange={(checked) => setForm({ ...form, isEnabled: checked })}
                />
                Enabled
              </label>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              type="submit"
              disabled={loading || form.priority <= 0 || form.providerId.trim().length === 0}
            >
              {loading ? 'Saving…' : submitLabel}
            </Button>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

export function ProvidersPage() {
  const { data: items, loading, error, refetch } = providerConfigsApi.useList();
  const { mutate: createItem, loading: creating } = providerConfigsApi.useCreate();
  const { mutate: updateItem } = providerConfigsApi.useUpdate();
  const { mutate: deleteItem } = providerConfigsApi.useDelete();
  const { showSnackbar } = useSnackbar();

  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const handleCreate = async (data: ProviderConfigInputType) => {
    try {
      await createItem(data);
      showSnackbar({ message: 'Provider config created', variant: 'success' });
      setShowCreate(false);
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to create provider config', variant: 'error' });
    }
  };

  const handleUpdate = async (id: string, data: ProviderConfigInputType) => {
    try {
      await updateItem(id, data);
      showSnackbar({ message: 'Provider config updated', variant: 'success' });
      setEditingId(null);
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to update provider config', variant: 'error' });
    }
  };

  const handleToggleEnabled = async (item: ProviderConfigEntity) => {
    setTogglingId(item.id);
    try {
      await updateItem(item.id, {
        channel: item.channel,
        providerId: item.providerId,
        priority: item.priority,
        isEnabled: !item.isEnabled,
      });
      showSnackbar({
        message: `Provider ${!item.isEnabled ? 'enabled' : 'disabled'}`,
        variant: 'success',
      });
      refetch();
    } catch {
      showSnackbar({ message: 'Failed to update provider config', variant: 'error' });
    } finally {
      setTogglingId(null);
    }
  };

  const toInput = (item: ProviderConfigEntity): ProviderConfigInputType => ({
    channel: item.channel,
    providerId: item.providerId,
    priority: item.priority,
    isEnabled: item.isEnabled,
  });

  if (error) {
    return (
      <ListPage
        title="Providers"
        description="Map notification channels to delivery providers and set failover priority."
      >
        <EmptyState
          title="Couldn't load provider configs"
          description={error instanceof Error ? error.message : 'Try again in a moment.'}
          action={<Button onClick={() => refetch()}>Retry</Button>}
          className="border-destructive/40 bg-destructive/5"
        />
      </ListPage>
    );
  }

  const list = (items ?? []) as ProviderConfigEntity[];
  const channelsWithoutProvider = REQUIRED_CHANNELS.filter(
    (channel) => !list.some((item) => item.channel === channel && item.isEnabled),
  );

  return (
    <ListPage
      title="Providers"
      description="Map notification channels to delivery providers and set failover priority."
      actions={
        <div className="flex items-center gap-2">
          <RefreshButton onRefresh={refetch} isRefreshing={loading} />
          <Button
            onClick={() => {
              setShowCreate(true);
              setEditingId(null);
            }}
          >
            <Plus /> Add provider
          </Button>
        </div>
      }
    >
      {!loading && channelsWithoutProvider.length > 0 ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Missing provider configuration</AlertTitle>
          <AlertDescription>
            Deliveries on {channelsWithoutProvider.join(', ')} will fail with{' '}
            <code className="font-mono">NO_PROVIDER</code>. Add and enable a provider for{' '}
            {channelsWithoutProvider.length === 1 ? 'this channel' : 'these channels'}.
          </AlertDescription>
        </Alert>
      ) : null}

      {showCreate ? (
        <ProviderConfigForm
          initial={EMPTY_FORM}
          onSubmit={handleCreate}
          onCancel={() => setShowCreate(false)}
          loading={creating}
          submitLabel="Create"
        />
      ) : null}

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Channel</TableHead>
              <TableHead>Provider ID</TableHead>
              <TableHead className="text-right">Priority</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <TableRow key={`s-${i}`}>
                  {Array.from({ length: 5 }).map((__, j) => (
                    <TableCell key={`s-${i}-${j}`}>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : list.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="p-0">
                  <EmptyState
                    title="No providers configured"
                    description="Add a provider config to route a channel's deliveries to a connector."
                    action={
                      <Button onClick={() => setShowCreate(true)}>
                        <Plus /> Add provider
                      </Button>
                    }
                    className="border-0 bg-transparent"
                  />
                </TableCell>
              </TableRow>
            ) : (
              [...list]
                .sort((a, b) => a.channel.localeCompare(b.channel) || a.priority - b.priority)
                .flatMap((item) => {
                  if (editingId === item.id) {
                    return [
                      <TableRow key={`edit-${item.id}`}>
                        <TableCell colSpan={5} className="p-0">
                          <ProviderConfigForm
                            initial={toInput(item)}
                            onSubmit={(data) => handleUpdate(item.id, data)}
                            onCancel={() => setEditingId(null)}
                            loading={false}
                            submitLabel="Save"
                          />
                        </TableCell>
                      </TableRow>,
                    ];
                  }

                  return [
                    <TableRow key={item.id}>
                      <TableCell>
                        <ChannelBadge channel={item.channel as ChannelKind} />
                      </TableCell>
                      <TableCell className="font-mono text-sm">{item.providerId}</TableCell>
                      <TableCell className="text-right tabular-nums">{item.priority}</TableCell>
                      <TableCell>
                        <Switch
                          checked={item.isEnabled}
                          disabled={togglingId === item.id}
                          onCheckedChange={() => handleToggleEnabled(item)}
                          aria-label={`Toggle ${item.channel} provider ${item.providerId}`}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingId(item.id);
                              setShowCreate(false);
                            }}
                          >
                            <Pencil /> Edit
                          </Button>
                          <ConfirmAction
                            trigger={
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive hover:text-destructive"
                              >
                                <Trash2 /> Delete
                              </Button>
                            }
                            title="Delete this provider config?"
                            description="This action cannot be undone. If it's the last enabled provider for this channel, deliveries will fail with NO_PROVIDER."
                            confirmLabel="Delete"
                            destructive
                            onConfirm={async () => {
                              await deleteItem(item.id);
                              refetch();
                            }}
                          />
                        </div>
                      </TableCell>
                    </TableRow>,
                  ];
                })
            )}
          </TableBody>
        </Table>
      </Card>
    </ListPage>
  );
}
